import { FormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  COMPARTMENT_SSCCID_PATTERN,
  ListQueryParam,
  NUMBER_PATTERN,
  VALIDATION_RULES,
} from '../../../../../common/constants';
import { AddressModel, ControlProperty, ListRange, SearchCriteriaBase } from '../../../../../common/models';
import { mustNotBeBlankValidator, duplicateAddressValidator } from '../../../../../common/utils';

export interface CompartmentListURIParams {
  hubId: number;
  searchCriteria: CompartmentListSearchCriteria;
}

export class CompartmentOverview {
  id?: number;
  name: string;
  ssccId: string;
  active = true;
  compartmentAddresses: AddressModel[] = [];
  yardTime1: number;
  yardTime2 = 0;
  onTime = 15;
  crossDockEnabled: boolean;
  compartmentSapClientViews: CompartmentSapModel[] = [];
  geofenceConfig: GeofenceConfigModel;
  enableGeofenceConfig = false;
  enableEmail = false;
  erpId: string;
  erpClientId: string;
  autoAllocationEnabled = false;

  constructor(params?: CompartmentOverview) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class GeofenceConfigModel {
  geofence1: number;
  geofence2: number;
  geofenceNotifications: GeofenceNotificationsModel[] = [];
  latitude: number;
  longitude: number;

  constructor(params?: GeofenceConfigModel) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class GeofenceNotificationsModel {
  id: number;
  emailAddress: string;
  languageCode: string;
}

export class GeofenceNotificationsForm {
  constructor(
    model: GeofenceNotificationsModel,
    public id: ControlProperty<number> = model.id,
    public emailAddress: ControlProperty<string> = model.emailAddress,
    public languageCode: ControlProperty<string> = model.languageCode,
  ) {}
}

export class CompartmentBrief extends CompartmentOverview {
  constructor(params?: CompartmentBrief) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class CompartmentOverviewForm {
  constructor(
    model: CompartmentOverview,
    fb: UntypedFormBuilder,
    hasUpdatePermission: boolean = true,
    public id: ControlProperty<number> = [{ value: model.id, disabled: !hasUpdatePermission }, []],
    public name: ControlProperty<string> = [model.name, [Validators.required, mustNotBeBlankValidator]],
    public active: ControlProperty<boolean> = [model.active, []],
    public ssccId: ControlProperty<string> = [
      model.ssccId,
      [Validators.required, Validators.pattern(COMPARTMENT_SSCCID_PATTERN)],
    ],
    public compartmentAddresses: ControlProperty<AddressModel[]> = [
      model.compartmentAddresses,
      [Validators.required, duplicateAddressValidator],
    ],
    public yardTime1: ControlProperty<number> = [
      model.yardTime1,
      [
        Validators.required,
        Validators.pattern(NUMBER_PATTERN),
        Validators.maxLength(VALIDATION_RULES.minutes.maxLength),
      ],
    ],
    public yardTime2: ControlProperty<number> = [
      model.yardTime2,
      [
        Validators.required,
        Validators.pattern(NUMBER_PATTERN),
        Validators.maxLength(VALIDATION_RULES.minutes.maxLength),
      ],
    ],
    public onTime: ControlProperty<number> = [
      model.onTime,
      [
        Validators.required,
        Validators.pattern(NUMBER_PATTERN),
        Validators.maxLength(VALIDATION_RULES.minutes.maxLength),
      ],
    ],
    public compartmentSapClientViews: ControlProperty<CompartmentSapModel[]> = [model.compartmentSapClientViews, []],
    public crossDockEnabled: ControlProperty<boolean> = [model.crossDockEnabled, []],
    public geofenceConfig: UntypedFormGroup = fb.group(new GeofenceConfigForm(model, fb)),
    public enableGeofenceConfig: ControlProperty<boolean> = model.enableGeofenceConfig,
    public enableEmail: ControlProperty<boolean> = model.enableEmail,
    public autoAllocationEnabled: ControlProperty<boolean> = model.autoAllocationEnabled,
  ) {}
}

export class GeofenceConfigForm {
  constructor(
    model: CompartmentOverview,
    fb: UntypedFormBuilder,
    public geofence1: ControlProperty<number> = model.geofenceConfig?.geofence1,
    public geofence2: ControlProperty<number> = model.geofenceConfig?.geofence2,
    public geofenceNotifications: FormArray = fb.array([]),
    public latitude: ControlProperty<number> = model.geofenceConfig?.latitude,
    public longitude: ControlProperty<number> = model.geofenceConfig?.longitude,
  ) {
    model.geofenceConfig?.geofenceNotifications?.forEach((notification: GeofenceNotificationsModel) => {
      geofenceNotifications.push(fb.group(new GeofenceNotificationsForm(notification)));
    });
  }
}

export class CompartmentListSearchCriteria extends SearchCriteriaBase {
  [ListQueryParam.Filter]: string;

  constructor(params?: CompartmentListSearchCriteria | ListRange) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class CompartmentSapModel {
  id: number;
  sapTransportationPlanningPoint: string;
  sapClientId: string;
  sapClientType: string;

  constructor(params?: CompartmentSapModel) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class CompartmentSapForm {
  constructor(
    sapModel: CompartmentSapModel,
    hasUpdatePermission: boolean = true,
    public id: ControlProperty<number> = [{ value: sapModel.id, disabled: !hasUpdatePermission }, []],
    public sapClientId: ControlProperty<string> = [
      { value: sapModel.sapClientId, disabled: !hasUpdatePermission },
      [Validators.required],
    ],
    public sapClientType: ControlProperty<string> = [
      { value: sapModel.sapClientType, disabled: !hasUpdatePermission },
      [Validators.required],
    ],
    public sapTransportationPlanningPoint: ControlProperty<string> = [
      { value: sapModel.sapTransportationPlanningPoint, disabled: !hasUpdatePermission },
      [Validators.required],
    ],
  ) {}
}
